(() => {
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)');
  const siteHeader = () => document.querySelector('sticky-header, .section-header');

  const setupReveals = (root, reducedMotion) => {
    const items = Array.from(root.querySelectorAll('[data-kec-reveal]'));
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return null;
    }

    const observer = new IntersectionObserver((entries, revealObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -9% 0px', threshold: 0.12 });

    items.forEach((item) => observer.observe(item));
    return observer;
  };

  const scrollToTarget = (link, reducedMotion) => {
    const href = link.getAttribute('href');
    if (!href || !href.startsWith('#')) return false;
    const target = document.getElementById(href.slice(1));
    if (!target) return false;

    target.scrollIntoView({
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
      block: 'start'
    });
    history.replaceState(null, '', href);
    return true;
  };

  if (!customElements.get('koota-commitments-hero')) {
    class KootaCommitmentsHero extends HTMLElement {
      constructor() {
        super();
        this.abortController = new AbortController();
        this.reducedMotion = prefersReducedMotion();
        this.frame = null;
      }

      connectedCallback() {
        if (this.abortController.signal.aborted) this.abortController = new AbortController();
        this.classList.add('is-enhanced');
        this.revealObserver = setupReveals(this, this.reducedMotion);
        this.header = siteHeader();
        this.setHeaderOffset();

        const signal = this.abortController.signal;
        this.querySelectorAll('a[href^="#"]').forEach((link) => {
          link.addEventListener('click', (event) => {
            if (!scrollToTarget(link, this.reducedMotion)) return;
            event.preventDefault();
          }, { signal });
        });

        window.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal });
        window.addEventListener('resize', () => {
          this.setHeaderOffset();
          this.requestUpdate();
        }, { passive: true, signal });

        if ('ResizeObserver' in window && this.header) {
          this.headerObserver = new ResizeObserver(() => this.setHeaderOffset());
          this.headerObserver.observe(this.header);
        }

        this.requestUpdate();
      }

      disconnectedCallback() {
        this.abortController.abort();
        this.revealObserver?.disconnect();
        this.headerObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      setHeaderOffset() {
        const height = this.header?.getBoundingClientRect().height || 0;
        this.style.setProperty('--kec-header-offset', `${Math.max(0, Math.round(height))}px`);
      }

      requestUpdate() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (this.reducedMotion.matches) return;
          const bounds = this.getBoundingClientRect();
          const progress = Math.min(1, Math.max(0, -bounds.top / Math.max(1, bounds.height)));
          this.style.setProperty('--kec-hero-shift', (progress * 42).toFixed(2));
        });
      }
    }

    customElements.define('koota-commitments-hero', KootaCommitmentsHero);
  }

  if (!customElements.get('koota-commitments-nav')) {
    class KootaCommitmentsNav extends HTMLElement {
      constructor() {
        super();
        this.abortController = new AbortController();
        this.reducedMotion = prefersReducedMotion();
        this.frame = null;
        this.activeId = '';
      }

      connectedCallback() {
        if (this.abortController.signal.aborted) this.abortController = new AbortController();
        this.links = Array.from(this.querySelectorAll('a[href^="#"]'));
        this.wrapper = this.closest('.section-koota-commitments-nav');
        this.header = siteHeader();
        this.refreshTargets();
        this.setHeaderOffset();

        const signal = this.abortController.signal;
        this.links.forEach((link) => {
          link.addEventListener('click', (event) => {
            if (!scrollToTarget(link, this.reducedMotion)) return;
            event.preventDefault();
          }, { signal });
        });

        window.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal });
        window.addEventListener('resize', () => {
          this.refreshTargets();
          this.setHeaderOffset();
          this.requestUpdate();
        }, { passive: true, signal });
        document.addEventListener('shopify:section:load', () => {
          this.refreshTargets();
          this.requestUpdate();
        }, { signal });
        document.addEventListener('shopify:section:reorder', () => {
          this.refreshTargets();
          this.requestUpdate();
        }, { signal });

        if ('ResizeObserver' in window && this.header) {
          this.headerObserver = new ResizeObserver(() => this.setHeaderOffset());
          this.headerObserver.observe(this.header);
        }

        this.requestUpdate();
      }

      disconnectedCallback() {
        this.abortController.abort();
        this.headerObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      refreshTargets() {
        this.targets = this.links
          .map((link) => document.getElementById(link.getAttribute('href').slice(1)))
          .filter(Boolean);
      }

      setHeaderOffset() {
        const height = this.header?.getBoundingClientRect().height || 0;
        const offset = `${Math.max(0, Math.round(height))}px`;
        this.style.setProperty('--kec-header-offset', offset);
        this.wrapper?.style.setProperty('--kec-header-offset', offset);
      }

      requestUpdate() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateNavigation();
        });
      }

      updateNavigation() {
        if (!this.targets?.length) return;

        const viewportLine = window.innerHeight * 0.38;
        const activeTarget = this.targets.find((target) => {
          const bounds = target.getBoundingClientRect();
          return bounds.top <= viewportLine && bounds.bottom >= viewportLine;
        }) || this.targets.reduce((nearest, target) => {
          const distance = Math.abs(target.getBoundingClientRect().top - viewportLine);
          return !nearest || distance < nearest.distance ? { target, distance } : nearest;
        }, null)?.target;

        if (activeTarget && activeTarget.id !== this.activeId) {
          this.activeId = activeTarget.id;
          this.links.forEach((link) => {
            const active = link.getAttribute('href') === `#${this.activeId}`;
            link.classList.toggle('is-active', active);
            if (active) {
              link.setAttribute('aria-current', 'location');
              if (window.innerWidth < 750) {
                link.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
              }
            } else {
              link.removeAttribute('aria-current');
            }
          });
        }

        const first = this.targets[0];
        const last = this.targets[this.targets.length - 1];
        const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
        const start = first.getBoundingClientRect().top + scrollY - window.innerHeight * 0.35;
        const end = last.getBoundingClientRect().bottom + scrollY - window.innerHeight * 0.65;
        const progress = Math.min(1, Math.max(0, (scrollY - start) / Math.max(1, end - start)));
        this.style.setProperty('--kec-progress', progress.toFixed(4));
      }
    }

    customElements.define('koota-commitments-nav', KootaCommitmentsNav);
  }

  if (!customElements.get('koota-commitment')) {
    class KootaCommitment extends HTMLElement {
      constructor() {
        super();
        this.abortController = new AbortController();
        this.reducedMotion = prefersReducedMotion();
        this.frame = null;
      }

      connectedCallback() {
        if (this.abortController.signal.aborted) this.abortController = new AbortController();
        this.classList.add('is-enhanced');
        this.revealObserver = setupReveals(this, this.reducedMotion);
        this.media = this.querySelector('[data-kec-media]');
        this.header = siteHeader();
        this.nav = document.querySelector('.section-koota-commitments-nav');
        this.setStickyOffset();
        this.setupCounters();

        const signal = this.abortController.signal;
        window.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal });
        window.addEventListener('resize', () => {
          this.setStickyOffset();
          this.requestUpdate();
        }, { passive: true, signal });

        this.requestUpdate();
      }

      disconnectedCallback() {
        this.abortController.abort();
        this.revealObserver?.disconnect();
        this.counterObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      setStickyOffset() {
        const headerHeight = this.header?.getBoundingClientRect().height || 0;
        const navHeight = this.nav?.getBoundingClientRect().height || 0;
        this.style.setProperty('--kec-sticky-top', `${Math.round(headerHeight + navHeight + 24)}px`);
      }

      setupCounters() {
        const counters = Array.from(this.querySelectorAll('[data-kec-count]'));
        if (!counters.length || this.reducedMotion.matches || !('IntersectionObserver' in window)) return;

        counters.forEach((counter) => {
          const original = counter.textContent.trim();
          const match = original.match(/^([^0-9-]*)(-?\d+(?:[.,]\d+)?)(.*)$/);
          if (!match) return;

          const decimals = (match[2].split(/[.,]/)[1] || '').length;
          counter.dataset.kecPrefix = match[1];
          counter.dataset.kecTarget = match[2].replace(',', '.');
          counter.dataset.kecSuffix = match[3];
          counter.dataset.kecDecimals = decimals.toString();
          counter.textContent = `${match[1]}${(0).toFixed(decimals)}${match[3]}`;
        });

        const animatedCounters = counters.filter((counter) => counter.dataset.kecTarget);
        this.counterObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.animateCounter(entry.target);
            observer.unobserve(entry.target);
          });
        }, { threshold: 0.6 });

        animatedCounters.forEach((counter) => this.counterObserver.observe(counter));
      }

      animateCounter(counter) {
        const target = Number(counter.dataset.kecTarget || 0);
        const prefix = counter.dataset.kecPrefix || '';
        const suffix = counter.dataset.kecSuffix || '';
        const decimals = Number(counter.dataset.kecDecimals || 0);
        const startTime = performance.now();
        const duration = 1050;

        const tick = (now) => {
          const progress = Math.min(1, (now - startTime) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          const value = (target * eased).toFixed(decimals).replace('.', ',');
          counter.textContent = `${prefix}${value}${suffix}`;
          if (progress < 1 && this.isConnected) requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
      }

      requestUpdate() {
        if (this.frame || !this.media) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (this.reducedMotion.matches) return;
          const bounds = this.media.getBoundingClientRect();
          const progress = Math.min(1, Math.max(0, (window.innerHeight - bounds.top) / (window.innerHeight + bounds.height)));
          this.style.setProperty('--kec-media-shift', `${((progress - 0.5) * 24).toFixed(2)}px`);
        });
      }
    }

    customElements.define('koota-commitment', KootaCommitment);
  }
})();
